import { useEffect, useMemo, useState } from "react";
import "../PlansBilling/CustomFeatureModal.scss";
import getDataFromApi from "../../../../components/getDataFromApi";

export default function TenantEditConfig({
    showModal,
    onClose,
    onSubmit,
    tenantId,
    mode = "ALL", // "BILLING_ONLY" | "EXCLUDE_BILLING" | "ALL"
    prefill,      // ✅ tenantdata from TenantDetails
}) {
    const [schema, setSchema] = useState(null);
    const [formData, setFormData] = useState({});
    const [formLoading, setFormLoading] = useState(false);
    const [schemaError, setSchemaError] = useState(null);
    const [initialFormData, setInitialFormData] = useState({});

    const [submitLoading, setSubmitLoading] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [errors, setErrors] = useState({});

    const [paymentLoading, setPaymentLoading] = useState(false);
    const [paymentError, setPaymentError] = useState(null);

    const findSchemaKey = (jsonSchema, matcher) => {
        const keys = Object.keys(jsonSchema?.properties || {});
        return keys.find((key) => matcher(key.toLowerCase()));
    };


    const getBillingKey = () =>
        schema ? findSchemaKey(schema, (key) => key.includes("billing")) : "Billing Details";

    const normalizeFieldName = (value) =>
        String(value || "").toLowerCase().replace(/[^a-z0-9]/g, "");

    const findObjectKey = (obj, candidates = []) => {
        const keys = Object.keys(obj || {});
        const normalizedCandidates = candidates.map(normalizeFieldName);

        return keys.find((key) =>
            normalizedCandidates.includes(normalizeFieldName(key))
        );
    };

    const pickKnownField = (obj, candidates, fallbackKey) => {
        const key = findObjectKey(obj, candidates) || fallbackKey;
        return [key, obj?.[key]];
    };

    const getDefaultAddressSchema = () => ({
        type: "object",
        title: "Address",
        properties: {
            line1: { type: "string", title: "Line 1" },
            city: { type: "string", title: "City" },
            state: { type: "string", title: "State" },
            country: { type: "string", title: "Country" },
            postal_code: { type: "string", title: "Postal Code" },
        },
    });

    const ensureBillingFields = (jsonSchema) => {
        const billingKey = findSchemaKey(jsonSchema, (key) => key.includes("billing"));
        if (!billingKey) return jsonSchema;

        const billingNode = jsonSchema.properties?.[billingKey];
        if (!billingNode) return jsonSchema;

        billingNode.type = billingNode.type || "object";
        billingNode.properties = billingNode.properties || {};

        const topAddressKey = findObjectKey(jsonSchema.properties, ["Address", "address"]);
        const billingAddressKey = findObjectKey(billingNode.properties, ["Address", "address"]);

        if (!billingAddressKey) {
            billingNode.properties.Address =
                (topAddressKey && jsonSchema.properties[topAddressKey]) ||
                getDefaultAddressSchema();
        }

        if (topAddressKey) {
            delete jsonSchema.properties[topAddressKey];
        }

        const topAutopayKey = findObjectKey(jsonSchema.properties, ["Autopay", "Auto Pay", "auto_pay", "autopay"]);
        const billingAutopayKey = findObjectKey(billingNode.properties, ["Autopay", "Auto Pay", "auto_pay", "autopay"]);

        if (!billingAutopayKey) {
            billingNode.properties.Autopay =
                (topAutopayKey && jsonSchema.properties[topAutopayKey]) || {
                    type: "boolean",
                    title: "Auto Pay",
                    default: true,
                };
        }

        if (topAutopayKey) {
            delete jsonSchema.properties[topAutopayKey];
        }

        return jsonSchema;
    };

    const endpoint = useMemo(() => {
        const id = tenantId || "6870ab0b60afb5ea41304ed4";
        return `${window.ENV.API_URL}/forms/admin/update_tenant/${id}`;
    }, [tenantId]);

    const buildDefaultsFromSchema = (jsonSchema) => {
        const walk = (node) => {
            if (!node) return undefined;

            if (node.type === "object") {
                const obj = {};
                Object.entries(node.properties || {}).forEach(([k, v]) => {
                    obj[k] = walk(v);
                });
                return obj;
            }

            if (node.default !== undefined) return node.default;
            if (node.type === "boolean") return false;
            if (node.type === "array") return [];
            return "";
        };

        return walk(jsonSchema);
    };

    const deepMerge = (base, patch) => {
        if (typeof base !== "object" || base === null) return patch ?? base;
        if (typeof patch !== "object" || patch === null) return base;

        const out = Array.isArray(base) ? [...base] : { ...base };
        Object.keys(patch).forEach((k) => {
            const bv = base?.[k];
            const pv = patch?.[k];
            if (typeof bv === "object" && bv !== null && typeof pv === "object" && pv !== null) {
                out[k] = deepMerge(bv, pv);
            } else if (pv !== undefined) {
                out[k] = pv;
            }
        });
        return out;
    };

    const prefillToFormData = (t) => {
        if (!t) return {};

        return {
            "Tenant Name": t?.name ?? "",
            "Admin Email": t?.admin_email ?? "",
            "Tenant Status": t?.status ?? "active",
            "Billing Details": {
                "Name": t?.billing_details?.name ?? "",
                "Email": t?.billing_details?.email ?? t?.admin_email ?? "",
                "Autopay": t?.billing_details?.autopay ?? true,

                // ✅ FIXED ADDRESS PREFILL
                "Address": {
                    "line1": t?.billing_details?.address?.line1 ?? t?.billing_details?.Address?.line1 ?? "",
                    "city": t?.billing_details?.address?.city ?? t?.billing_details?.Address?.city ?? "",
                    "state": t?.billing_details?.address?.state ?? t?.billing_details?.Address?.state ?? "",
                    "country": t?.billing_details?.address?.country ?? t?.billing_details?.Address?.country ?? "",
                    "postal_code": t?.billing_details?.address?.postal_code ?? t?.billing_details?.Address?.postal_code ?? "",
                }
            },
        };
    };

    const getRenderableTopKeys = (jsonSchema) => {
        const keys = Object.keys(jsonSchema?.properties || {});
        const billingKey = findSchemaKey(jsonSchema, (key) => key.includes("billing"));

        if (mode === "BILLING_ONLY") return billingKey ? [billingKey] : [];
        if (mode === "EXCLUDE_BILLING") {
            return keys.filter((key) =>
                ["tenantname", "adminemail"].includes(normalizeFieldName(key))
            );
        }
        return keys;
    };

    // ---------- FETCH SCHEMA ----------
    useEffect(() => {
        if (!showModal) return;
        if (mode === "SUSPEND_ONLY" || mode === "DELETE_ONLY") return;

        // always clear errors on open
        setSubmitError(null);
        setErrors({});
        setSchemaError(null);

        // fetch only if schema not loaded yet
        if (schema) return;

        setFormLoading(true);

        getDataFromApi(endpoint, "get")
            .then((result) => {
                const s = structuredClone(result.json_schema);

                ensureBillingFields(s);

                setSchema(s);
                setFormLoading(false);
            })
            .catch((e) => {
                setFormLoading(false);
                setSchemaError(e?.message || "Failed to load form schema.");
            });
    }, [showModal, schema, endpoint]);



    const handleDeleteTenant = () => {
        if (!tenantId) return;

        setSubmitLoading(true);
        setSubmitError(null);

        getDataFromApi(
            `${window.ENV.API_URL}/admin/tenant/${tenantId}`,
            "delete"
        )
            .then(() => {
                setSubmitLoading(false);
                onClose?.();
                onSubmit?.();
            })
            .catch((e) => {
                setSubmitLoading(false);
                setSubmitError(e?.message || "Failed to deactivate tenant.");
            });
    };



    const handleSuspend = () => {
        if (!tenantId) return;

        setSubmitLoading(true);
        setSubmitError(null);

        getDataFromApi(endpoint, "post", {
            "Tenant Status": "suspended"
        })
            .then(() => {
                setSubmitLoading(false);
                onClose?.();
                onSubmit?.();
            })
            .catch((e) => {
                setSubmitLoading(false);
                setSubmitError(e?.message || "Failed to suspend tenant.");
            });
    };



    const handleUpdatePayment = () => {
        if (!tenantId) return;

        setPaymentLoading(true);
        setPaymentError(null);

        getDataFromApi(
            `${window.ENV.API_URL}/admin/tenant/update_payment`,
            "post",
            {
                tenant_id: tenantId
            }
        )
            .then((res) => {
                setPaymentLoading(false);

                if (res?.status === "success" && res?.url) {
                    // ✅ Redirect to Stripe Billing Portal
                    window.location.href = res.url;
                } else {
                    setPaymentError("Invalid response from server.");
                }
            })
            .catch((e) => {
                setPaymentLoading(false);
                setPaymentError(
                    getFriendlyApiError(
                        e,
                        "Failed to update payment method. Please try again."
                    )
                );
            });
    };


    // ✅ IMPORTANT: initialize formData EVERY time modal opens (or mode/prefill changes)
    useEffect(() => {
        if (!showModal) return;
        if (!schema) return;

        const defaults = buildDefaultsFromSchema(schema);
        const merged = deepMerge(defaults, prefillToFormData(prefill));
        const billingKey = findSchemaKey(schema, (key) => key.includes("billing"));

        if (billingKey && billingKey !== "Billing Details") {
            merged[billingKey] = deepMerge(
                merged[billingKey] || {},
                merged["Billing Details"] || {}
            );
            delete merged["Billing Details"];
        }

        setFormData(merged);
        setInitialFormData(structuredClone(merged));
    }, [showModal, mode, schema, prefill]);

    const hasPayloadChanged = () => {
        return JSON.stringify(buildSubmitPayload(formData)) !==
            JSON.stringify(buildSubmitPayload(initialFormData));
    };

    const setDeep = (obj, path, value) => {
        const parts = Array.isArray(path) ? path : String(path).split(".");
        const next = structuredClone(obj || {});
        let cur = next;

        for (let i = 0; i < parts.length - 1; i++) {
            const p = parts[i];
            if (typeof cur[p] !== "object" || cur[p] === null) cur[p] = {};
            cur = cur[p];
        }
        cur[parts[parts.length - 1]] = value;
        return next;
    };

    const getDeep = (obj, path) => {
        const parts = Array.isArray(path) ? path : String(path).split(".");
        return parts.reduce((acc, p) => (acc && acc[p] !== undefined ? acc[p] : undefined), obj);
    };

    const renderableKeys = schema ? getRenderableTopKeys(schema) : [];

    // validate only rendered
    const validateForm = () => {
        if (!schema) return true;

        const newErrors = {};
        let hasError = false;

        const validateNode = (node, path = []) => {
            if (!node) return;

            if (node.type === "object") {
                const required = Array.isArray(node.required) ? node.required : [];
                required.forEach((field) => {
                    const v = getDeep(formData, [...path, field]);
                    const missing =
                        v === undefined ||
                        v === null ||
                        (typeof v === "string" && v.trim() === "");
                    if (missing) {
                        hasError = true;
                        newErrors[[...path, field].join(".")] = true;
                    }
                });

                Object.entries(node.properties || {}).forEach(([k, v]) =>
                    validateNode(v, [...path, k])
                );
            }
        };

        renderableKeys.forEach((k) => validateNode(schema.properties?.[k], [k]));

        setErrors(newErrors);
        setSubmitError(hasError ? "Please fill all required fields." : null);
        return !hasError;
    };

    const buildBillingPayload = (sourceData = {}) => {
        const billingKey = findSchemaKey(schema, (key) =>
            key.toLowerCase().includes("billing")
        );
        const billingData = billingKey
            ? sourceData?.[billingKey] || sourceData?.["Billing Details"] || {}
            : sourceData?.["Billing Details"] || {};

        const [, nameValue] = pickKnownField(billingData, ["Name", "name"], "Name");
        const [, emailValue] = pickKnownField(billingData, ["Email", "email"], "Email");
        const [, autopayValue] = pickKnownField(
            billingData,
            ["Autopay", "Auto Pay", "auto_pay", "autopay"],
            "Autopay"
        );
        const [, addressValue] = pickKnownField(
            billingData,
            ["Address", "address"],
            "Address"
        );

        const payloadBillingKey = billingKey || "BILLING_DETAILS_LABELS";

        return {
            [payloadBillingKey]: {
                name: nameValue ?? "",
                email: emailValue ?? "",
                autopay: autopayValue ?? false,
                address: addressValue || {}
            }
        };
    };

    const buildTenantConfigPayload = (sourceData = {}) => {
        const payload = {};

        Object.entries(sourceData || {}).forEach(([key, value]) => {
            if (["tenantname", "adminemail"].includes(normalizeFieldName(key))) {
                payload[key] = value;
            }
        });

        return payload;
    };

    const buildSubmitPayload = (sourceData = formData) => {
        // Billing popup only: send name, email, autopay, and address inside the schema billing key.
        if (mode === "BILLING_ONLY") {
            return buildBillingPayload(sourceData);
        }

        // Edit tenant popup: send only tenant name and admin email.
        if (mode === "EXCLUDE_BILLING") {
            return buildTenantConfigPayload(sourceData);
        }

        return sourceData;
    };

    useEffect(() => {
        if (!showModal) return;

        setSubmitError(null);
        setErrors({});
        setPaymentError(null);
        setSchemaError(null);
    }, [showModal, mode]);


    function getFriendlyApiError(
        error,
        fallbackMessage = "Failed to update tenant. Please try again."
    ) {
        const raw = error?.message || String(error);

        try {
            const match = raw.match(/\{.*\}/);
            const parsed = match ? JSON.parse(match[0]) : error;

            const details = parsed?.detail;

            if (Array.isArray(details)) {
                return details
                    .map((item) => {
                        const field = item?.loc?.slice(1).join(" → ");
                        return `${field}: ${item?.msg}`;
                    })
                    .join("\n");
            }

            if (typeof details === "string") return details;
        } catch (_) { }

        if (/traceback|file ".*"|starlette|fastapi|\/opt\/venv|python/i.test(raw)) {
            const statusMatch = raw.match(/HTTP\s+(\d+)/i);
            return statusMatch
                ? `Server error (${statusMatch[1]}). Please try again.`
                : fallbackMessage;
        }

        return raw || fallbackMessage;
    }

    const handleSubmit = () => {

        if (!hasPayloadChanged()) {
            setSubmitError("No changes made.");
            return;
        }


        if (!validateForm()) return;

        setSubmitLoading(true);
        setSubmitError(null);

        getDataFromApi(endpoint, "post", buildSubmitPayload())
            .then(() => {
                setSubmitLoading(false);
                onClose?.();
                onSubmit?.();
            })
            .catch((e) => {
                setSubmitLoading(false);
                setSubmitError(getFriendlyApiError(e));
            });
    };

    const clearFieldError = (pathKey) => {
        setErrors((prev) => {
            if (!prev[pathKey]) return prev;
            const next = { ...prev };
            delete next[pathKey];
            return next;
        });
    };

    const renderField = (key, node, path = []) => {
        if (!node) return null;
        const fullPath = [...path, key];
        const pathKey = fullPath.join(".");
        const value = getDeep(formData, fullPath);
        const isError = !!errors[pathKey];
        const isPostalCode = normalizeFieldName(key) === "postalcode";

        if (node?.type === "object") {
            const isAddress = normalizeFieldName(key) === "address";
            const showHeading = !normalizeFieldName(key).includes("billing");

            return (
                <div key={pathKey} className="mt-2">

                    {showHeading && (
                        <div
                            className="fw-bold text-secondary mb-2 text-uppercase"
                            style={{ fontSize: "12px" }}
                        >
                            {isAddress ? "Address" : (node.title || key)}
                        </div>
                    )}

                    <div className={isAddress ? "row g-3" : "d-flex flex-column gap-3"}>
                        {Object.entries(node.properties || {}).map(([childKey, childNode]) =>
                            isAddress ? (
                                <div key={childKey} className="col-sm-6">
                                    {renderField(childKey, childNode, fullPath)}
                                </div>
                            ) : (
                                renderField(childKey, childNode, fullPath)
                            )
                        )}
                    </div>
                </div>
            );
        }


        if (node?.enum?.length) {
            // ✅ Hide "suspended" option for Tenant Status
            let enumValues = node.enum;
            let enumNames = node.enumNames;

            if (key === "Tenant Status") {
                enumValues = node.enum.filter((val) => val !== "suspended");
                enumNames = node.enumNames
                    ? node.enumNames.filter((_, i) => node.enum[i] !== "suspended")
                    : undefined;
            }

            return (
                <label key={pathKey} className={isError ? "error" : ""}>
                    {(node.title || key).toUpperCase()}
                    <select
                        className="text"
                        value={value ?? node.default ?? ""}
                        onChange={(e) => {
                            setFormData((prev) => setDeep(prev, fullPath, e.target.value));
                            clearFieldError(pathKey);
                            setSubmitError(null);
                        }}
                    >
                        {enumValues.map((val, i) => (
                            <option key={String(val)} value={val}>
                                {enumNames?.[i] ?? String(val)}
                            </option>
                        ))}
                    </select>
                </label>
            );
        }

        if (node?.type === "boolean") {
            return (
                <label key={pathKey} className={`switch ${isError ? "error" : ""}`}>
                    <input
                        type="checkbox"
                        checked={!!value}
                        onChange={(e) => {
                            setFormData((prev) => setDeep(prev, fullPath, e.target.checked));
                            clearFieldError(pathKey);
                            setSubmitError(null);
                        }}
                    />
                    <span />
                    {node.title || key}
                </label>
            );
        }

        return (
            <label key={pathKey} className={isError ? "error" : ""}>
                {(node.title || key).toUpperCase()}
                <input
                    className="text"
                    inputMode={isPostalCode ? "numeric" : undefined}
                    pattern={isPostalCode ? "[0-9]*" : undefined}
                    value={value ?? ""}
                    onChange={(e) => {
                        const nextValue = isPostalCode
                            ? e.target.value.replace(/\D/g, "")
                            : e.target.value;
                        setFormData((prev) => setDeep(prev, fullPath, nextValue));
                        clearFieldError(pathKey);
                        setSubmitError(null);
                    }}
                />
            </label>
        );
    };

    const renderBillingOnlyFields = () => {
        const billingKey = getBillingKey();
        const billingProperties = schema?.properties?.[billingKey]?.properties || {};

        const nameKey = findObjectKey(billingProperties, ["Name", "name"]) || "Name";
        const emailKey = findObjectKey(billingProperties, ["Email", "email"]) || "Email";
        const autopayKey =
            findObjectKey(billingProperties, ["Autopay", "Auto Pay", "auto_pay", "autopay"]) ||
            "Autopay";
        const addressKey = findObjectKey(billingProperties, ["Address", "address"]) || "Address";

        return (
            <>
                {renderField(nameKey, billingProperties[nameKey] || { type: "string", title: "Name" }, [billingKey])}
                {renderField(emailKey, billingProperties[emailKey] || { type: "string", title: "Email" }, [billingKey])}
                {renderField(
                    autopayKey,
                    billingProperties[autopayKey] || { type: "boolean", title: "Auto Pay", default: true },
                    [billingKey]
                )}
                {renderField(
                    addressKey,
                    billingProperties[addressKey] || getDefaultAddressSchema(),
                    [billingKey]
                )}
            </>
        );
    };

    if (!showModal) return null;

    if (formLoading) {
        return (
            <div className="center-form">
                <div >Loading…</div>
            </div>
        );
    }

    // ✅ show error instead of disappearing
    if (schemaError) {
        return (
            <div className="center-form">
                <div >
                    <div className="cf-head">
                        <div>
                            <span className="cf-tag text-primary">LABS & ENGINEERING</span>
                            <h2>Form Error</h2>
                        </div>
                        <button className="cf-close text-secondary" onClick={onClose}>✕</button>
                    </div>

                    <div className="cf-body">
                        <div className="text-danger fw-bold">{schemaError}</div>
                    </div>

                    <div className="cf-footer">
                        <button className="cf-submit bg-primary" onClick={onClose}>
                            Close
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!["SUSPEND_ONLY", "DELETE_ONLY"].includes(mode) && !schema) return null;

    const title =
        mode === "SUSPEND_ONLY"
            ? "Suspend Organization"
            : mode === "DELETE_ONLY"
                ? "Inactive Tenant"
                : mode === "BILLING_ONLY"
                    ? "Manage Billing Details"
                    : mode === "EXCLUDE_BILLING"
                        ? "Edit Tenant Config"
                        : "Edit Tenant";

    return (
        <>
            <div className="center-form">
                <div style={{ maxWidth: '600px' }}>
                    <div className="cf-head">
                        <div>
                            {/* <span className="cf-tag text-primary">LABS & ENGINEERING</span> */}
                            <h2>
                                {title}
                            </h2>
                        </div>
                        <button className="cf-close text-secondary" onClick={onClose}>✕</button>
                    </div>

                    <div className="cf-body">

                        {mode === "SUSPEND_ONLY" ? (
                            <>
                                <p className="fw-semibold">
                                    Are you sure you want to suspend this organization?
                                </p>
                                <p className="text-secondary small-font">
                                    All users will immediately lose access.
                                    You can reactivate later from admin panel.
                                </p>

                                {submitError && (
                                    <div
                                        className="text-danger text-center w-100 fw-bold mt-2"
                                        style={{ fontSize: "small", whiteSpace: "pre-line" }}
                                    >
                                        {submitError}
                                    </div>
                                )}
                            </>
                        ) : mode === "DELETE_ONLY" ? (
                            <>
                                <p className="fw-semibold">
                                    Are you sure you want to mark this tenant as inactive?
                                </p>
                                <p className="text-secondary small-font">
                                    The organization will no longer be active for users. You can review or restore access later from the admin panel if supported.
                                </p>

                                {submitError && (
                                    <div className="text-danger text-center w-100 fw-bold mt-2">
                                        {submitError}
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                {mode === "BILLING_ONLY" ? (
                                    renderBillingOnlyFields()
                                ) : renderableKeys.length > 0 ? (
                                    renderableKeys.map((key) =>
                                        renderField(key, schema.properties?.[key], [])
                                    )
                                ) : (
                                    <div className="text-secondary text-center w-100 py-4">
                                        No editable fields are available for this action.
                                    </div>
                                )}

                                {submitError && (
                                    <div
                                        className="text-danger text-center w-100 fw-bold mt-2"
                                        style={{ fontSize: "small" }}
                                    >
                                        {submitError}
                                    </div>
                                )}
                            </>
                        )}

                    </div>

                    <div className="cf-footer">
                        {mode === "SUSPEND_ONLY" ? (
                            <div className="d-a justify-content-between w-100">
                                <button
                                    className="container-card px-4 py-2"
                                    onClick={onClose}
                                    disabled={submitLoading}
                                >
                                    Cancel
                                </button>

                                <button
                                    className="cf-submit bg-warning px-4"
                                    onClick={handleSuspend}
                                    disabled={submitLoading}
                                >
                                    {submitLoading ? "Suspending…" : "Confirm Suspend"}
                                </button>
                            </div>
                        ) : mode === "DELETE_ONLY" ? (
                            <div className="d-a justify-content-between w-100">
                                <button
                                    className="container-card px-4 py-2"
                                    onClick={onClose}
                                    disabled={submitLoading}
                                >
                                    Cancel
                                </button>

                                <button
                                    className="cf-submit bg-warning px-4"
                                    onClick={handleDeleteTenant}
                                    disabled={submitLoading}
                                >
                                    {submitLoading ? "Deactivating…" : "Confirm Deactivate"}
                                </button>
                            </div>
                        ) : (
                            <>
                                {paymentError && (
                                    <div className="text-danger text-center w-100 fw-bold mt-2">
                                        {paymentError}
                                    </div>
                                )}

                                {mode === "BILLING_ONLY" ? (
                                    <button
                                        className="cf-submit text-primary bg-transparent border"
                                        onClick={handleUpdatePayment}
                                        disabled={paymentLoading}
                                    >
                                        {paymentLoading ? "Redirecting…" : "Update Payment Method"}
                                    </button>
                                ) : (
                                    <span></span>
                                )}

                                <button
                                    className="cf-submit bg-primary"
                                    onClick={handleSubmit}
                                    disabled={submitLoading}
                                >
                                    {submitLoading ? "Saving…" : "Save Changes"}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <div className="mobile-view-bottom"></div>
        </>
    );
}
